import { useCallback, useEffect, useRef, useState } from 'react';

import { toolkit } from 'services/api';
import { ToolkitNote } from 'types';

import { useEventTracking } from './useEventTracking';

type NoteEditableFields = Pick<ToolkitNote, 'title' | 'body'>;

const sortNotes = (list: ToolkitNote[]): ToolkitNote[] => {
  return [...list].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
};

interface SelectOptions {
  trackEvent?: boolean;
}

export const useToolkitNotes = () => {
  const [notes, setNotes] = useState<ToolkitNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);

  const removedNoteIdsRef = useRef<Set<string>>(new Set());
  const sessionRef = useRef<{ noteId: string; startedAt: number } | null>(null);
  const isMountedRef = useRef(true);
  const { track } = useEventTracking();

  const mergeUpdatedNote = useCallback((updated: ToolkitNote) => {
    if (removedNoteIdsRef.current.has(updated.id)) {
      return;
    }

    setNotes((prev) => {
      const exists = prev.some((note) => note.id === updated.id);
      const next = exists ? prev.map((note) => (note.id === updated.id ? updated : note)) : [updated, ...prev];
      return sortNotes(next);
    });
  }, [removedNoteIdsRef]);

  const closeSession = useCallback(() => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }

    sessionRef.current = null;

    const durationSeconds = Math.max(0, Math.round((Date.now() - session.startedAt) / 1000));
    track('toolkit_note_close', {
      note_id: session.noteId,
      time_spent_seconds: durationSeconds,
    });

    if (durationSeconds === 0) {
      return;
    }

    toolkit
      .recordSession(session.noteId, {
        duration_seconds: durationSeconds,
        ended_at: new Date().toISOString(),
      })
      .then((response) => {
        if (response?.data) {
          mergeUpdatedNote(response.data);
        }
      })
      .catch((err) => {
        console.error('Failed to record toolkit note session', err);
      });
  }, [mergeUpdatedNote, track]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await toolkit.listNotes();
      const data = response?.data ?? [];
      removedNoteIdsRef.current.clear();
      setNotes(sortNotes(data));
      setError(null);
    } catch (err) {
      console.error('Failed to load toolkit notes', err);
      setError('Failed to load notes');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      void closeSession();
    };
  }, [closeSession]);

  const selectNote = useCallback(
    (noteId: string, options?: SelectOptions) => {
      if (!noteId || noteId === selectedNoteId) {
        return;
      }

      closeSession();

      const shouldTrack = options?.trackEvent ?? true;
      setSelectedNoteId(noteId);
      sessionRef.current = { noteId, startedAt: Date.now() };

      if (shouldTrack) {
        track('toolkit_note_click', {
          note_id: noteId,
        });
      }
    },
    [closeSession, selectedNoteId, track],
  );

  const createNote = useCallback(async () => {
    try {
      const response = await toolkit.createNote();
      const note = response?.data;
      if (!note) {
        throw new Error('Invalid response when creating note');
      }

      track('button_click', { action: 'toolkit_note_create', note_id: note.id });
      track('toolkit_note_create', { note_id: note.id });

      removedNoteIdsRef.current.delete(note.id);
      setNotes((prev) => sortNotes([note, ...prev]));
      return note;
    } catch (err) {
      console.error('Failed to create toolkit note', err);
      setError('Failed to create note');
      return null;
    }
  }, [track]);

  const updateNote = useCallback(
    async (noteId: string, updates: Partial<NoteEditableFields>) => {
      if (!noteId) {
        return null;
      }
      try {
        const response = await toolkit.updateNote(noteId, updates);
        const updated = response?.data;
        if (updated) {
          mergeUpdatedNote(updated);
        }
        return updated ?? null;
      } catch (err) {
        console.error('Failed to update toolkit note', err);
        setError('Failed to update note');
        throw err;
      }
    },
    [mergeUpdatedNote],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      if (!noteId) {
        return false;
      }

      const wasActive = selectedNoteId === noteId;
      if (wasActive) {
        closeSession();
      }

      try {
        await toolkit.deleteNote(noteId);
        removedNoteIdsRef.current.add(noteId);
        setNotes((prev) => prev.filter((note) => note.id !== noteId));
        setSelectedNoteId((current) => (current === noteId ? null : current));
        return true;
      } catch (err) {
        console.error('Failed to delete toolkit note', err);
        setError('Failed to delete note');
        return false;
      }
    },
    [closeSession, selectedNoteId],
  );

  const refresh = useCallback(async () => {
    await fetchNotes();
  }, [fetchNotes]);

  return {
    notes,
    loading,
    error,
    selectedNoteId,
    selectNote,
    createNote,
    updateNote,
    deleteNote,
    refresh,
  };
};
